import React from 'react';
import { PayloadComparisonViewer } from '../../../payload-comparison-viewer';
import { CodingEmbeddedPanel } from './coding-detail-embedded';
import * as S from '../claim-timeline.styles';
import * as Card from './timeline-detail-card.styles';
import { ICodingDetailProps } from './coding-detail.types';
import { getCodingPayload, parseCodingNotes } from './coding-detail.utils';

/**
 * CodingDetail Component
 *
 * Displays detailed information about a coding record including:
 * - Coding ID, Vendor, Status
 * - Notes (handles double-encoded JSON)
 * - Before/After/Diff comparison
 *
 * Used by both Provider and Support apps in their timeline views.
 */
export const CodingDetail: React.FunctionComponent<ICodingDetailProps> = (
    props: ICodingDetailProps,
): React.ReactElement => {
    const { coding, embedded = false } = props;

    if (embedded) {
        return <CodingEmbeddedPanel coding={coding} embedded />;
    }

    const beforePayload = getCodingPayload(coding, 'before');
    const afterPayload = getCodingPayload(coding, 'after');
    const beforeText = beforePayload != null ? JSON.stringify(beforePayload, null, 4) : '';
    const afterText = afterPayload != null ? JSON.stringify(afterPayload, null, 4) : '';
    const showDiff = !!beforePayload && !!afterPayload;

    const rules = parseCodingNotes(coding.notes);
    const codingNotes =
        rules.length > 0
            ? rules.map((rule) => `Rule ${rule.n}: ${rule.text}`).join('\n')
            : coding.notes;

    return (
        <Card.CdPanel $embedded key={coding.id}>
            <Card.CdSectionCard $embedded>
                <S.ExpandTable expand={true}>
                    <S.ExpandTBody>
                        <S.ExpandTR>
                            <S.ExpandTH>Coding ID</S.ExpandTH>
                            <S.ExpandTD>{coding.id}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Vendor</S.ExpandTH>
                            <S.ExpandTD>{coding.vendor}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Status</S.ExpandTH>
                            <S.ExpandTD>{coding.status}</S.ExpandTD>
                        </S.ExpandTR>
                        {codingNotes && (
                            <S.ExpandTR>
                                <S.ExpandTH>Notes</S.ExpandTH>
                                <S.ExpandTD>
                                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                                        {codingNotes}
                                    </pre>
                                </S.ExpandTD>
                            </S.ExpandTR>
                        )}
                    </S.ExpandTBody>
                </S.ExpandTable>
            </Card.CdSectionCard>
            <Card.CdSectionCard $embedded>
                <PayloadComparisonViewer
                    currentX12={afterText}
                    previousX12={beforeText}
                    showDiff={showDiff}
                    previousLabel="Before"
                    currentLabel="After"
                />
            </Card.CdSectionCard>
        </Card.CdPanel>
    );
};
